import { computed, Injectable, Signal } from '@angular/core';
import { TableSettingsKey } from '@core/enums/table-settings-key.enum';
import { KeyValuePair, TableSettings } from '@core/interfaces/table-settings.model';
import { deepMerge } from '@core/utils/deep-merge';
import { getState, patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { TableSettingsStateItem } from '@store/table-settings/interfaces/table-settings-state.model';
import { FilterFieldsInterface } from 'src/app/components/table/models/filter-fields.model';
import { SortModel } from 'src/app/components/table/models/sort.model';
import { TableSettingsDefault } from 'src/app/components/table/models/table-settings';

export interface TableSettingsState {
    [key: string]: TableSettingsStateItem;
}

const initialState: TableSettingsState = {};

@Injectable({ providedIn: 'root' })
export class TableSettingsStore extends signalStore(
    withState(initialState),
    withMethods((store) => {
        return {
            setTableSettings(key: TableSettingsKey, settings?: TableSettingsStateItem, isReplace?: boolean) {
                patchState(store, (state) => ({
                    ...state,
                    [key]: state[key] && !isReplace ? deepMerge(state[key], settings || {}) : settings || TableSettingsDefault,
                }));
            },
            setTableRequestSettings(key: TableSettingsKey, requestSettings: TableSettings<string>, isLoadMore?: boolean) {
                patchState(store, (state) => ({
                    ...state,
                    [key]: state[key]?.requestSettings
                        ? {
                              ...state[key],
                              requestSettings: deepMerge(state[key].requestSettings, requestSettings || {}),
                              isLoadMore: isLoadMore,
                          }
                        : {
                              ...state[key],
                              requestSettings: TableSettingsDefault.requestSettings,
                              isLoadMore: isLoadMore,
                          },
                }));
            },
            setTableSort(key: TableSettingsKey, sort: SortModel) {
                patchState(store, (state) => {
                    const requestSettings = {
                        orderBy: !sort.direction ? null : sort.active,
                        reverse: sort.direction === 'asc',
                    };

                    return {
                        ...state,
                        [key]: state[key]?.requestSettings
                            ? {
                                  ...state[key],
                                  requestSettings: deepMerge(state[key].requestSettings, requestSettings || {}),
                              }
                            : {
                                  ...state[key],
                                  requestSettings: TableSettingsDefault.requestSettings,
                              },
                    };
                });
            },
            setTableFilter(key: TableSettingsKey, filterValues: KeyValuePair<string>[]) {
                patchState(store, (state) => {
                    const requestSettings = {
                        pageNumber: 1,
                        filters: filterValues,
                    };

                    return {
                        ...state,
                        [key]: state[key]?.requestSettings
                            ? {
                                  ...state[key],
                                  requestSettings: deepMerge(state[key].requestSettings, requestSettings || {}),
                              }
                            : {
                                  ...state[key],
                                  requestSettings: TableSettingsDefault.requestSettings,
                              },
                    };
                });
            },
            setColumnSettings(key: TableSettingsKey, columnSettings: FilterFieldsInterface[]) {
                patchState(store, (state) => ({
                    ...state,
                    [key]: state[key]
                        ? {
                              ...state[key],
                              columnSettings,
                          }
                        : {
                              ...TableSettingsDefault,
                              columnSettings,
                          },
                }));
            },
            getTableSettingsForSection(sectionId: TableSettingsKey): Signal<TableSettingsStateItem | null> {
                return computed(() => getState(store)[sectionId] ?? null);
            },
            getTableRequestSettingsForSection(sectionId: TableSettingsKey): Signal<TableSettings<string> | null> {
                return computed(() => getState(store)[sectionId]?.requestSettings ?? null);
            },
            getColumnsSettingsForSection(sectionId: TableSettingsKey): Signal<FilterFieldsInterface[] | null> {
                return computed(() => getState(store)[sectionId]?.columnSettings ?? null);
            },
            getActiveFiltersTableForSection(sectionId: TableSettingsKey): Signal<KeyValuePair<string>[]> {
                return computed(
                    () =>
                        getState(store)[sectionId]?.requestSettings?.filters?.filter((item) =>
                            Array.isArray(item?.value)
                                ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                  item?.value.length > 0 && item?.value.some((item: any) => item.value)
                                : item?.value !== '' && item?.value !== null && item?.value !== undefined,
                        ) ?? [],
                );
            },
        };
    }),
) {}
